/**
 * An S3 POST policy, as the `sign` endpoints answer it.
 *
 * Only `bucket` is required, because it forms the POST URL rather than travelling as a form
 * field. Every policy field is `,omitempty` on the wire (`s3/sign.go`), and a POST policy accepts
 * only the fields it has conditions for — so each is optional here and the uploader appends
 * whichever are present. `Content-Type` is the one that matters in practice: endpoints whose
 * policy carries a content-type condition get one from `getUploadFunction`, while the imports
 * policy has none and fails the POST with "Extra input fields: content-type" if sent one.
 */
export type S3UploadOptions = {
  bucket: string;
  acl?: string;
  awsaccesskeyid?: string;
  expires?: string;
  key?: string;
  policy?: string;
  signature?: string;
  success_action_status?: string;
  'Content-Type'?: string;
};

class S3UploadError extends Error {
  detail: string;
  status: number;

  constructor({ detail, status }: { detail: string; status: number }, message?: string) {
    super(message);
    this.name = 'S3UploadError';
    this.detail = detail;
    this.status = status;
  }
}

/**
 * Uploads a file to AWS S3 using a signed POST policy.
 *
 * Ported from the Ember `utils/uploads/s3` helper. Non-EU datacenters upload to
 * S3; see `getUploadFn` for how the target is chosen.
 */
export async function uploadToS3(
  file: File,
  json: S3UploadOptions,
  options: { processData?: (data: FormData) => void } = {},
): Promise<{ key: string }> {
  const data = new FormData();

  for (const [key, value] of Object.entries(json)) {
    // `bucket` only forms the URL; it's not a valid S3 POST form field and AWS
    // rejects fields without a matching policy condition. A field the policy did not carry is
    // skipped rather than appended: `FormData.append` would stringify it to "undefined" and the
    // policy would reject the POST for a field it has no condition for.
    if (key === 'bucket' || value == null) {
      continue;
    }
    data.append(key, value);
  }

  options.processData?.(data);
  data.append('file', file);

  const response = await fetch(`https://s3.amazonaws.com/${json.bucket}`, { method: 'POST', body: data });
  const text = await response.text();
  const xml = new DOMParser().parseFromString(text, 'text/xml');

  if (response.ok) {
    // The bucket only answers with the XML document carrying `Location` when the policy asked it
    // to, through `success_action_status`; without that field it replies 204 with an empty body
    // and there is no key to report. An empty string passed for one would be stored as the
    // uploaded object and read back as a source pointing at nothing, so a missing location is a
    // failed upload — the same answer `uploadToSignedBucket` gives.
    const location = xml.getElementsByTagName('Location')[0]?.textContent;
    if (location == null || location === '') {
      throw new S3UploadError(
        { detail: 'Your file reached the bucket but it did not say where. Try again.', status: response.status },
        // The body is what a 204 gives, which is nothing — so the condition is the message.
        'S3 upload succeeded but returned no Location.',
      );
    }

    return { key: location };
  }

  const detail =
    xml.getElementsByTagName('Message')[0]?.textContent ??
    'Your file could not be uploaded. Check your connection and try again.';
  throw new S3UploadError({ detail, status: response.status }, text);
}
